/**
 * ProductSelectorStep Component
 *
 * Step 1 of the onboarding wizard - select products to monitor.
 * Features:
 * - Search/filter products by title or SKU
 * - Select all / deselect all functionality
 * - Stock level indicators
 * - Expandable product list (4 or 10 products visible)
 * - Selected products card stack with count badge
 * - Hoverable stack expansion to browse selected products
 * - Scrollable product list
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  BlockStack,
  InlineStack,
  Text,
  Button,
  Icon,
  Box,
  TextField,
  Checkbox,
  Scrollable,
  Thumbnail,
} from '@shopify/polaris';
import { SearchIcon, PackageIcon, ChevronLeftIcon, ChevronRightIcon } from '@shopify/polaris-icons';
import type { WizardProduct } from './types';
import { BRAND_PURPLE } from '../../constants/brand';

// Height constant for product list
const LIST_HEIGHT = 560; // ~10 products visible
const PRODUCTS_PER_PAGE = 20;

export interface ProductSelectorStepProps {
  /** Available products to select from */
  products: WizardProduct[];
  /** Currently selected product IDs */
  selectedProducts: Set<string>;
  /** Callback when a product is toggled */
  onSelectionChange: (productId: string) => void;
}

export function ProductSelectorStep({
  products,
  selectedProducts,
  onSelectionChange,
}: ProductSelectorStepProps) {
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isStackHovered, setIsStackHovered] = useState(false);
  const [hoveredProductId, setHoveredProductId] = useState<string | null>(null);
  const [hoverEnabled, setHoverEnabled] = useState(false);
  const [badgeAnimationKey, setBadgeAnimationKey] = useState(0);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isStackExiting, setIsStackExiting] = useState(false);
  const [showStack, setShowStack] = useState(false);
  const prevCountRef = useRef(selectedProducts.size);
  const initialHoverRef = useRef<string | null>(null);
  const productRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollableRef = useRef<HTMLDivElement>(null);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const stackContainerRef = useRef<HTMLDivElement>(null);
  const stackHoverTimerRef = useRef<NodeJS.Timeout | null>(null);
  const stackExitTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle stack visibility with exit animation
  useEffect(() => {
    if (selectedProducts.size > 0) {
      // Cancel any pending exit
      if (stackExitTimerRef.current) {
        clearTimeout(stackExitTimerRef.current);
        stackExitTimerRef.current = null;
      }
      setIsStackExiting(false);
      setShowStack(true);
    } else if (showStack) {
      // Start exit animation
      setIsStackExiting(true);
      stackExitTimerRef.current = setTimeout(() => {
        setShowStack(false);
        setIsStackExiting(false);
      }, 200); // Match animation duration
    }
    
    return () => {
      if (stackExitTimerRef.current) {
        clearTimeout(stackExitTimerRef.current);
      }
    };
  }, [selectedProducts.size, showStack]);

  // Trigger badge pop animation when count changes
  useEffect(() => {
    if (selectedProducts.size !== prevCountRef.current) {
      setBadgeAnimationKey(k => k + 1);
      prevCountRef.current = selectedProducts.size;
    }
  }, [selectedProducts.size]);

  const filteredProducts = products.filter(
    (product) =>
      product.title.toLowerCase().includes(searchValue.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchValue.toLowerCase()) ||
      product.variant.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleSelectAll = useCallback(() => {
    filteredProducts.forEach((product) => {
      if (!selectedProducts.has(product.id)) {
        onSelectionChange(product.id);
      }
    });
  }, [filteredProducts, selectedProducts, onSelectionChange]);

  const handleDeselectAll = useCallback(() => {
    filteredProducts.forEach((product) => {
      if (selectedProducts.has(product.id)) {
        onSelectionChange(product.id);
      }
    });
  }, [filteredProducts, selectedProducts, onSelectionChange]);

  /**
   * Get stock display text - neutral styling without color coding
   */
  const getStockDisplay = (stock: number) => {
    return (
      <Text as="span" variant="bodySm" fontWeight="medium">
        {stock} units
      </Text>
    );
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * PRODUCTS_PER_PAGE,
    currentPage * PRODUCTS_PER_PAGE
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue]);

  // Get selected products for the card stack
  const selectedProductsList = useMemo(() => {
    return products.filter(p => selectedProducts.has(p.id));
  }, [products, selectedProducts]);

  // Scroll to a product in the list when clicked from the stack
  const scrollToProduct = useCallback((productId: string) => {
    const rowElement = productRowRefs.current.get(productId);
    if (rowElement) {
      rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a brief highlight effect using inline styles
      rowElement.style.backgroundColor = 'rgba(139, 123, 216, 0.25)';
      rowElement.style.boxShadow = '0 0 0 2px rgba(139, 123, 216, 0.3)';
      rowElement.style.transition = 'background-color 0.3s ease-out, box-shadow 0.3s ease-out';
      
      setTimeout(() => {
        rowElement.style.backgroundColor = 'rgba(139, 123, 216, 0.06)';
        rowElement.style.boxShadow = 'none';
      }, 800);
      
      setTimeout(() => {
        rowElement.style.backgroundColor = '';
        rowElement.style.boxShadow = '';
        rowElement.style.transition = '';
      }, 1500);
    }
  }, []);

  return (
    <BlockStack gap="400">
      {/* Header with card stack */}
      <InlineStack align="space-between" blockAlign="start">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd" fontWeight="semibold">
            Select products to monitor
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            Choose which products you want to track for low stock alerts
          </Text>
        </BlockStack>

        {/* Selected products card stack - fixed size placeholder to prevent layout shift */}
        <div style={{ position: 'relative', width: '72px', height: '56px', flexShrink: 0 }}>
          {showStack && (
            <div 
              ref={stackContainerRef}
              className={`selected-products-stack ${isStackHovered ? 'stack-expanded' : ''} ${isStackExiting ? 'stack-exiting' : ''}`}
              style={{ 
                position: 'absolute',
                top: 0,
                right: 0,
                zIndex: 100,
                // Expanded hover zone with padding for grace area
                padding: '12px',
                margin: '-12px',
              }}
              onMouseEnter={() => {
                // Cancel any pending close
                if (stackHoverTimerRef.current) {
                  clearTimeout(stackHoverTimerRef.current);
                  stackHoverTimerRef.current = null;
                }
                setIsStackHovered(true);
                setHoverEnabled(false);
                initialHoverRef.current = null;
              }}
              onMouseLeave={() => {
                // Grace period before closing
                stackHoverTimerRef.current = setTimeout(() => {
                  setIsStackHovered(false);
                  setHoveredProductId(null);
                  setHoverEnabled(false);
                  setTooltipVisible(false);
                  initialHoverRef.current = null;
                  if (tooltipTimerRef.current) {
                    clearTimeout(tooltipTimerRef.current);
                    tooltipTimerRef.current = null;
                  }
                }, 200);
              }}
            >
            {/* Count badge - always in top right */}
            <div
              key={badgeAnimationKey}
              className="stack-count-badge"
              style={{
                position: 'absolute',
                top: '-4px',
                right: isStackHovered ? '-4px' : (selectedProductsList.length === 1 ? '24px' : '12px'),
                minWidth: '22px',
                height: '22px',
                padding: '0 6px',
                borderRadius: '11px',
                backgroundColor: BRAND_PURPLE,
                color: 'white',
                fontSize: '12px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                border: '2px solid white',
                zIndex: 110,
                animation: 'badgePop 0.2s ease-out',
                transition: 'right 0.2s ease-out',
              }}
            >
              {selectedProductsList.length}
            </div>

            {/* Collapsed stack view */}
            {!isStackHovered ? (
              <div style={{ width: '72px', height: '56px', position: 'relative' }}>
                {/* Stacked thumbnails - show up to 3, reversed so latest is on top */}
                {selectedProductsList.slice(-3).reverse().map((product, index, arr) => {
                  const stackPosition = arr.length - 1 - index;
                  return (
                    <div
                      key={product.id}
                      className="stack-thumbnail"
                      style={{
                        position: 'absolute',
                        top: stackPosition * 4,
                        left: stackPosition * 6,
                        zIndex: 3 - stackPosition,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: index === 0 ? '0 2px 6px rgba(0, 0, 0, 0.18)' : '0 1px 3px rgba(0, 0, 0, 0.12)',
                        border: '2px solid white',
                        animation: 'stackSlideIn 0.25s ease-out',
                        animationFillMode: 'backwards',
                      }}
                    >
                      <Thumbnail
                        source={product.imageUrl || PackageIcon}
                        alt={product.title}
                        size="small"
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Expanded stack view on hover - absolutely positioned to not affect layout */
              <div 
                className="stack-expanded-view"
                style={{
                  position: 'absolute',
                  top: '0',
                  right: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '8px',
                  padding: '6px 10px',
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  animation: 'stackExpandIn 0.15s ease-out',
                  zIndex: 105,
                }}
              >
                {/* Scrollable product thumbnails */}
                <div 
                  className="stack-thumbnails-scroll"
                  style={{ 
                    display: 'flex', 
                    gap: '6px',
                    maxWidth: '260px',
                    overflowX: 'auto',
                    padding: '4px',
                  }}
                >
                  {selectedProductsList.map((product) => {
                    const isHighlighted = hoverEnabled && hoveredProductId === product.id;
                    return (
                      <div
                        key={product.id}
                        onClick={() => scrollToProduct(product.id)}
                        onMouseEnter={() => {
                          if (initialHoverRef.current === null) {
                            initialHoverRef.current = product.id;
                          } else if (initialHoverRef.current !== product.id) {
                            setHoverEnabled(true);
                          }
                          setHoveredProductId(product.id);
                          // Clear any existing timer
                          if (tooltipTimerRef.current) {
                            clearTimeout(tooltipTimerRef.current);
                          }
                          // Start delay timer for tooltip
                          tooltipTimerRef.current = setTimeout(() => {
                            setTooltipVisible(true);
                          }, 400);
                        }}
                        onMouseMove={(e) => {
                          // Track cursor position relative to viewport
                          setTooltipPosition({ x: e.clientX, y: e.clientY });
                        }}
                        onMouseLeave={() => {
                          setHoveredProductId(null);
                          setTooltipVisible(false);
                          if (tooltipTimerRef.current) {
                            clearTimeout(tooltipTimerRef.current);
                            tooltipTimerRef.current = null;
                          }
                        }}
                        style={{
                          position: 'relative',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: isHighlighted ? `2px solid ${BRAND_PURPLE}` : '2px solid transparent',
                          boxShadow: isHighlighted ? '0 2px 8px rgba(139, 123, 216, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                          transition: 'all 0.15s ease-out',
                          transform: isHighlighted ? 'scale(1.05)' : 'scale(1)',
                          flexShrink: 0,
                        }}
                      >
                        <Thumbnail
                          source={product.imageUrl || PackageIcon}
                          alt={product.title}
                          size="small"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            </div>
          )}
        </div>

        {/* Cursor-following tooltip - rendered via portal */}
        {tooltipVisible && hoveredProductId && typeof document !== 'undefined' && createPortal(
          <div
            className="cursor-tooltip"
            style={{
              position: 'fixed',
              left: tooltipPosition.x + 12,
              top: tooltipPosition.y - 8,
              background: 'rgba(32, 34, 35, 0.92)',
              color: 'white',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              zIndex: 9999,
              pointerEvents: 'none',
              animation: 'tooltipFadeIn 0.12s ease-out',
            }}
          >
            {selectedProductsList.find(p => p.id === hoveredProductId)?.title}
          </div>,
          document.body
        )}
      </InlineStack>

      {/* Search */}
      <TextField
        label=""
        value={searchValue}
        onChange={setSearchValue}
        placeholder="Search products or SKU..."
        prefix={<Icon source={SearchIcon} />}
        autoComplete="off"
        clearButton
        onClearButtonClick={() => setSearchValue('')}
      />

      {/* Selection controls */}
      <InlineStack align="space-between" blockAlign="center">
        <Text as="p" variant="bodyMd" tone="subdued">
          {selectedProducts.size} of {products.length} products selected
        </Text>
        <InlineStack gap="200">
          <Button variant="plain" onClick={handleSelectAll}>
            Select all
          </Button>
          <Text as="span" tone="subdued">
            |
          </Text>
          <Button variant="plain" onClick={handleDeselectAll}>
            Deselect all
          </Button>
        </InlineStack>
      </InlineStack>

      {/* Product List */}
      <Box borderColor="border" borderWidth="025" borderRadius="200" minHeight={`${LIST_HEIGHT}px`}>
        {filteredProducts.length === 0 ? (
          <Box paddingBlock="800">
            <BlockStack gap="200" inlineAlign="center">
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                {searchValue.trim()
                  ? `No products match "${searchValue}"`
                  : 'No products available'}
              </Text>
            </BlockStack>
          </Box>
        ) : (
          <Scrollable 
            style={{ 
              height: `${LIST_HEIGHT}px`, 
              maxHeight: `${LIST_HEIGHT}px`,
            }}
          >
            <BlockStack gap="0">
              {paginatedProducts.map((product, index) => (
                <div
                  key={product.id}
                  ref={(el) => {
                    if (el) {
                      productRowRefs.current.set(product.id, el);
                    } else {
                      productRowRefs.current.delete(product.id);
                    }
                  }}
                  className={`product-row product-row-enter${selectedProducts.has(product.id) ? ' product-row-selected' : ''}`}
                  onClick={() => onSelectionChange(product.id)}
                  style={{ 
                    cursor: 'pointer',
                    animationDelay: `${index * 30}ms`,
                  }}
                >
                  <Box
                    paddingBlock="300"
                    paddingInline="400"
                    borderColor="border"
                    borderBlockEndWidth={index < paginatedProducts.length - 1 ? '025' : undefined}
                  >
                    <InlineStack align="space-between" blockAlign="center" wrap={false}>
                      {/* Checkbox and product info */}
                      <InlineStack gap="300" blockAlign="center" wrap={false}>
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            label=""
                            checked={selectedProducts.has(product.id)}
                            onChange={() => onSelectionChange(product.id)}
                          />
                        </div>
                        <Thumbnail
                          source={product.imageUrl || PackageIcon}
                          alt={product.title}
                          size="small"
                        />
                        <BlockStack gap="050">
                          <Text as="p" variant="bodyMd" fontWeight="regular" truncate>
                            {product.title}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {product.variantCount === 1 ? '1 variant' : `${product.variantCount} variants`}
                          </Text>
                        </BlockStack>
                    </InlineStack>

                    {/* Current stock */}
                    <Box minWidth="80px">
                      <BlockStack gap="050" inlineAlign="end">
                        <Text as="p" variant="bodySm" tone="subdued" alignment="end">
                          Current stock
                        </Text>
                        {getStockDisplay(product.currentStock)}
                      </BlockStack>
                    </Box>
                  </InlineStack>
                  </Box>
                </div>
              ))}
            </BlockStack>
          </Scrollable>
        )}
      </Box>

      {/* Pagination - show only if more than one page */}
      {totalPages > 1 && (
        <InlineStack align="center" gap="300" blockAlign="center">
          <Button
            icon={ChevronLeftIcon}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            accessibilityLabel="Previous page"
            variant="plain"
          />
          <Text as="span" variant="bodySm" tone="subdued">
            Page {currentPage} of {totalPages}
          </Text>
          <Button
            icon={ChevronRightIcon}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            accessibilityLabel="Next page"
            variant="plain"
          />
        </InlineStack>
      )}
    </BlockStack>
  );
}
